import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const AuditsDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [auditTypeFilter, setAuditTypeFilter] = useState('All Audit Types');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Mock Data for Audits --
    const mockAudits = [
        { key: '1', audit: 'Financial FY2024', entity: 'Reliance Industries Ltd', type: 'Financial', findingsCount: 0, findingsText: '0 Findings', owner: 'CFO' },
        { key: '2', audit: 'CSR Compliance 2024', entity: 'Reliance Retail', type: 'CSR', findingsCount: 2, findingsText: '2 Findings', owner: 'CSR Lead' },
        { key: '3', audit: 'Environmental Review', entity: 'Reliance New Energy', type: 'Legal', findingsCount: 3, findingsText: '3 Findings', owner: 'Sustainability Head' },
    ];

    const filteredAudits = mockAudits.filter(item => {
        const matchesSearch = item.audit.toLowerCase().includes(searchText.toLowerCase()) ||
            item.entity.toLowerCase().includes(searchText.toLowerCase());
        const matchesType = auditTypeFilter === 'All Audit Types' || item.type === auditTypeFilter;
        return matchesSearch && matchesType;
    });

    const renderTypeTag = (type: string) => {
        let color = 'default';
        if (type === 'Financial') color = 'success';
        if (type === 'CSR') color = 'warning';
        if (type === 'Legal') color = 'processing'; // Blueish

        return <Tag color={color} style={{ minWidth: '80px', textAlign: 'center' }}>{type}</Tag>;
    };

    const renderFindingsBadge = (count: number, text: string) => {
        let color = '#52c41a'; // 0 Findings - Green
        if (count > 0 && count < 3) color = '#faad14'; // Minor - Orange/Yellow
        if (count >= 3) color = '#f5222d'; // Major - Red

        return (
            <Tag color={color} style={{ borderRadius: '12px', padding: '0 12px', borderColor: 'transparent', color: '#fff', fontWeight: '500' }}>
                {text}
            </Tag>
        );
    };

    // Derived Metrics
    // Clean Audits: 0 findings
    const cleanAudits = filteredAudits.filter(a => a.findingsCount === 0).length;
    // Minor Findings: 1-2 findings
    const minorFindings = filteredAudits.filter(a => a.findingsCount > 0 && a.findingsCount < 3).length;
    // Major Findings: >= 3 findings
    const majorFindings = filteredAudits.filter(a => a.findingsCount >= 3).length;

    // Avg Findings
    const totalFindings = filteredAudits.reduce((acc, curr) => acc + curr.findingsCount, 0);
    const avgFindings = (totalFindings / (filteredAudits.length || 1)).toFixed(1);

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        PASSIONIT — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Audits</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Audits Table & Metrics */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Audits & Compliance</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search audit or entity..."
                                    style={{ width: 240 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                <Select defaultValue="All Audit Types" onChange={setAuditTypeFilter} style={{ width: 150 }}>
                                    <Option value="All Audit Types">All Audit Types</Option>
                                    <Option value="Financial">Financial</Option>
                                    <Option value="CSR">CSR</Option>
                                    <Option value="Legal">Legal</Option>
                                </Select>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredAudits.length} of {mockAudits.length} audits</Text>

                        <Table
                            dataSource={filteredAudits}
                            pagination={false}
                            columns={[
                                { title: 'AUDIT', dataIndex: 'audit', key: 'audit', render: (text) => <b>{text}</b> },
                                { title: 'ENTITY', dataIndex: 'entity', key: 'entity', render: (text) => <Text>{text}</Text> },
                                { title: 'TYPE', dataIndex: 'type', key: 'type', align: 'center', render: (type) => renderTypeTag(type) },
                                { title: 'FINDINGS', dataIndex: 'findingsText', key: 'findingsText', align: 'center', render: (text, record) => renderFindingsBadge(record.findingsCount, text) },
                                { title: 'OWNER', dataIndex: 'owner', key: 'owner', render: (text) => <Text>{text}</Text> },
                                { title: 'ACTION', key: 'action', align: 'center', render: () => <a href="#">View</a> }
                            ]}
                        />

                        {/* Summary Metrics */}
                        <Row gutter={16} className="mt-4">
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">Clean Audits</Text>
                                    <Title level={3} className="text-success m-0">{cleanAudits}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fffbe6' }}>
                                    <Text type="secondary" className="d-block mb-1">Minor Findings</Text>
                                    <Title level={3} className="text-warning m-0">{minorFindings}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fff1f0' }}>
                                    <Text type="secondary" className="d-block mb-1">Major Findings</Text>
                                    <Title level={3} className="text-danger m-0">{majorFindings}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Avg Findings</Text>
                                    <Title level={3} className="text-primary m-0">{avgFindings}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AuditsDashboard;
